// Third-party
import React from 'react';
import classNames from 'classnames';

// Utils
import useThemeVersion from '@/hooks/useThemeVersion';
import User from '@/types/user';
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import { CURRENCY } from '../../../utils/currency';
import { PORTFOLIO_TYPES } from '../../../utils/portfolios';
import { shouldExpandPortfolioInReadOnly } from '@/utils/comparison';

// Components
import ActionBarButton from './ActionBarButton';
import { Checkbox } from '../inputs';
import { Tooltip } from '../tooltips';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import LegacyActionBar from './LegacyActionBar';

type Props = {
  comparison: Comparison;
  items: ComparisonItem[];
  isEditPinned: boolean;
  user: User;
  unselectAll: () => void;
  onCombineClick: () => void;
  onDeleteClick: () => void;
  onCopyClick: (item: ComparisonItem) => void;
  onModifyClick: (item: ComparisonItem) => void;
  onExpandClick: (item: ComparisonItem, expand: boolean) => void;
  onToggleFXItem: (item: ComparisonItem, toggled: boolean) => void;
};

class ActionBarV2 extends React.PureComponent<Props> {
  render() {
    const { items, onCombineClick, unselectAll, onDeleteClick, onCopyClick, comparison, onModifyClick, isEditPinned, user, onExpandClick, onToggleFXItem } =
      this.props;

    if (items.length === 0) {
      return null;
    }

    const classes = classNames('action-bar align-items-center', {
      hide: !items.length || isEditPinned,
    });

    const showCombinePortfolio =
      items.length > 1 &&
      !items.some(item => {
        const excludedTypes = [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.BENCHMARK, COMPARISON_ITEM_TYPES.GROUP];
        return excludedTypes.includes(item.type as COMPARISON_ITEM_TYPES);
      });

    const editableItemTypes: string[] = [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.BENCHMARK, COMPARISON_ITEM_TYPES.GROUP];
    const hasSinglePortfolioSelected = items.length === 1 && items[0].type === COMPARISON_ITEM_TYPES.PORTFOLIO;
    const hasSingleBenchmarkPortfolioSelected =
      items.length === 1 && items[0].type === COMPARISON_ITEM_TYPES.BENCHMARK && items[0].portfolioType === PORTFOLIO_TYPES.CUSTOM;

    let showModify = items.length === 1 && editableItemTypes.includes(items[0].type) && !items[0].providedBy && !items[0].enterprise;
    let showClone = hasSinglePortfolioSelected || hasSingleBenchmarkPortfolioSelected;
    let showDelete = true;

    const showExpandPortfolio = hasSinglePortfolioSelected;
    const showFXToggle = comparison.showBaseCurrencies && items.length === 1 && items[0].currency !== CURRENCY.CAD;
    const isPortfolioExpandLimited = shouldExpandPortfolioInReadOnly(items[0], user);

    for (const portfolio of items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO)) {
      if (portfolio.availableActions != null) {
        showModify = showModify && portfolio.availableActions.edit;
        showClone = showClone && portfolio.availableActions.clone;
        showDelete = showDelete && portfolio.availableActions.delete;
      }
    }

    return (
      <div className={classes}>
        <Tooltip className="custom-tooltip-v2" message={formatMessage('Comparison.ActionBar.Unselect')} effect="solid" offset={{ top: 8, left: 4 }}>
          <div className="icon-button-container">
            <Checkbox ariaLabel={formatMessage('Common.Accessibility.ClearAllSelections')} unselect invert onChange={unselectAll} />
          </div>
        </Tooltip>

        <span className="number-selected">
          <FormattedMessage id="Comparison.ActionBar.NumberSelected" values={{ count: items.length }} />
        </span>

        <div className="button-container">
          {showCombinePortfolio && (
            <ActionBarButton
              icon="merge"
              onClick={onCombineClick}
              tooltipMessage={formatMessage('Comparison.ActionBar.CombineIntoPortfolio')}
              tooltipClassName="custom-tooltip-v2"
              offsetTop={8}
            />
          )}

          {showModify && (
            <ActionBarButton
              icon="ds-edit"
              onClick={() => onModifyClick(items[0])}
              tooltipMessage={formatMessage(`Comparison.ActionBar.Modify.${items[0].type.toUpperCase()}`)}
              tooltipClassName="custom-tooltip-v2"
              offsetTop={8}
            />
          )}

          {showClone && (
            <ActionBarButton
              icon="ds-copy"
              onClick={() => onCopyClick(items[0])}
              tooltipMessage={formatMessage(`Comparison.ActionBar.Copy.${items[0].type.toUpperCase()}`)}
              tooltipClassName="custom-tooltip-v2"
              offsetTop={8}
            />
          )}

          {showExpandPortfolio && (
            <ActionBarButton
              icon="expand"
              onClick={() => onExpandClick(items[0], !items[0].isExpanded)}
              tooltipMessage={formatMessage(
                isPortfolioExpandLimited
                  ? 'Comparison.PortfolioExpandLimited'
                  : `Comparison.ActionBar.${items[0].isExpanded ? 'CollapsePortfolio' : 'ExpandPortfolio'}`,
              )}
              tooltipClassName={classNames('custom-tooltip-v2', { 'has-long-info': isPortfolioExpandLimited })}
              offsetTop={8}
              active={items[0].isExpanded}
              disabled={isPortfolioExpandLimited}
            />
          )}

          {showFXToggle && (
            <ActionBarButton
              icon="fx"
              onClick={() => onToggleFXItem(items[0], !items[0].isFX)}
              tooltipMessage={formatMessage('Comparison.ActionBar.ShowCAD')}
              tooltipClassName="custom-tooltip-v2"
              offsetTop={8}
              active={items[0].isFX}
              className="fx-btn"
            />
          )}

          {showDelete && (
            <ActionBarButton
              icon="ds-remove-circle-outline"
              onClick={onDeleteClick}
              tooltipMessage={formatMessage('Common.Remove')}
              tooltipClassName="custom-tooltip-v2"
              offsetTop={8}
            />
          )}
        </div>
      </div>
    );
  }
}

const ActionBar = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ActionBarV2 {...props} />;
  }
  return <LegacyActionBar {...props} />;
};

export default ActionBar;
